import { useEffect } from "react";
import { useQuery } from "convex/react";
import { api } from "../../convex/_generated/api";
import { WidgetSync } from "@/plugins/widget-sync";
import { Capacitor } from "@capacitor/core";
import { format } from "date-fns";

export function useWidgetSync() {
  const isNative = Capacitor.isNativePlatform();

  // Get dashboard state for widget data
  const localDate = format(new Date(), "yyyy-MM-dd");
  const localTime = format(new Date(), "HH:mm");

  const dashboardState = useQuery(api.dashboard.getDashboardState, {
    localDate,
    localTime,
  });

  // Sync widget data whenever dashboard state changes
  useEffect(() => {
    if (!isNative || !dashboardState) return;

    const syncWidgetData = async () => {
      console.log("[WidgetSync] Starting sync, dashboardState:", !!dashboardState);
      try {
        // Sync active shift
        if (dashboardState.activeShift) {
          const shift = dashboardState.activeShift;
          await WidgetSync.syncActiveShift({
            isActive: true,
            isPaused: shift.status === "paused",
            type: (shift.type as "block" | "commute") ?? "block",
            totalMiles: shift.totalMiles,
            startedAt: shift.startedAt,
            station: dashboardState.activeBlock?.station,
            basePay: dashboardState.activeBlock?.basePay,
            bookedMinutes: dashboardState.activeBlock
              ? calculateMinutes(
                  dashboardState.activeBlock.shiftStart,
                  dashboardState.activeBlock.shiftEnd
                )
              : undefined,
          });
        } else {
          await WidgetSync.syncActiveShift({ isActive: false });
        }

        // Sync today summary
        await WidgetSync.syncTodaySummary({
          blockCount: dashboardState.todaySummary.blockCount,
          totalEarnings: dashboardState.todaySummary.totalEarnings,
          totalMinutes: dashboardState.todaySummary.totalMinutes,
          totalMiles: dashboardState.todaySummary.totalMiles,
        });

        // Sync weekly stats
        await WidgetSync.syncWeeklyStats({
          totalEarnings: dashboardState.weeklySnapshot.totalEarnings,
          hoursWorked: dashboardState.weeklySnapshot.totalHours,
          blockCount: dashboardState.weeklySnapshot.blockCount,
          avgHourlyRate: dashboardState.weeklySnapshot.hourlyRate,
        });

        // Sync weekly goal
        await WidgetSync.syncWeeklyGoal({
          goal: dashboardState.weeklySnapshot.weeklyGoal,
          currentEarnings: dashboardState.weeklySnapshot.totalEarnings,
        });

        // Sync hours remaining (with true rolling window data)
        await WidgetSync.syncHoursRemaining({
          dailyLimit: dashboardState.rollingHours.dailyLimit,
          weeklyLimit: dashboardState.rollingHours.weeklyLimit,
          hoursWorkedToday: dashboardState.rollingHours.hoursWorkedToday,
          hoursWorkedThisWeek:
            dashboardState.rollingHours.hoursWorkedLast7Days,
          hoursLeftToday: dashboardState.rollingHours.hoursLeftToday,
          hoursLeftThisWeek: dashboardState.rollingHours.hoursAvailable,
          bottleneck: dashboardState.rollingHours.bottleneck,
          nextReleaseTime: dashboardState.rollingHours.nextReleaseTime,
          nextReleaseHours: dashboardState.rollingHours.nextReleaseHours,
        });
        console.log("[WidgetSync] All syncs complete!");
      } catch (error) {
        console.error("[WidgetSync] Sync error:", error);
      }
    };

    syncWidgetData();
  }, [isNative, dashboardState]);
}

function calculateMinutes(start: string, end: string): number {
  const [startH, startM] = start.split(":").map(Number);
  const [endH, endM] = end.split(":").map(Number);
  return endH * 60 + endM - (startH * 60 + startM);
}
